import { Button } from "@/components/ui/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { BodyText } from "@/components/ui/Text/BodyText";
import { useS3SyncDownload } from "@/network/useS3SyncDownload";
import { FileDownIcon, LoaderCircle } from "lucide-react";

export const ResultsDataGridHeader = ({
  date,
  dxReportPdfUrls,
  diagnosticReportIds,
  launchDxReportViewer,
}: {
  date: string;
  dxReportPdfUrls: Set<string>;
  diagnosticReportIds: Set<string>;
  launchDxReportViewer: (diagnosticReportId: string) => void;
}) => {
  const { mutateAsync: downloadDxReportPdf, isPending } = useS3SyncDownload();

  const handleDownloadDxReportPdf = async (pdfUrl: string) => {
    downloadDxReportPdf({ path: pdfUrl }).then((data) => {
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
    });
  };

  const openDxReportViewer = (diagnosticReportId: string) => {
    launchDxReportViewer(diagnosticReportId);
  };

  return (
    <BodyText
      size="sm"
      textColor="link"
      bold
      underline
      className="flex items-center gap-1"
    >
      {diagnosticReportIds?.size === 1 ? (
        <Button
          variant="link"
          size="sm"
          onClick={() => {
            openDxReportViewer(Array.from(diagnosticReportIds)[0]);
          }}
        >
          {date}
        </Button>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="link" size="sm">
              {date}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {Array.from(diagnosticReportIds).map((diagnosticReportId) => (
              <DropdownMenuItem key={diagnosticReportId}>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    openDxReportViewer(diagnosticReportId);
                  }}
                >
                  {diagnosticReportId}
                </Button>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {dxReportPdfUrls?.size &&
        (dxReportPdfUrls?.size === 1 ? (
          <Button
            variant="icon"
            size="sm"
            className="m-0 p-0"
            disabled={isPending}
            onClick={async () => {
              await handleDownloadDxReportPdf(Array.from(dxReportPdfUrls)[0]);
            }}
          >
            {isPending ? (
              <LoaderCircle className="h-4 w-4 animate-spin stroke-link" />
            ) : (
              <FileDownIcon className="stroke-link h-4 w-4" />
            )}
          </Button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="icon"
                size="sm"
                className="m-0 p-0"
                disabled={isPending}
              >
                {isPending ? (
                  <LoaderCircle className="h-4 w-4 animate-spin stroke-link" />
                ) : (
                  <FileDownIcon className="stroke-link h-4 w-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {Array.from(dxReportPdfUrls).map((pdfUrl, index) => (
                <DropdownMenuItem key={pdfUrl}>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 m-0"
                    onClick={() => {
                      handleDownloadDxReportPdf(pdfUrl);
                    }}
                  >
                    Open Report {index + 1}
                  </Button>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ))}
    </BodyText>
  );
};
